import { StoreTapStatsResponse } from '../../types/marketing';
import { formatCount, formatRate, rangeDays, scaledTapTarget, targetBar } from './marketingMath';

interface KpiProps {
  id: string;
  label: string;
  value: string;
  sub?: string;
  /** Target marker; omitted for tiles without a target. */
  target?: { bar: ReturnType<typeof targetBar>; text: string };
}

function Kpi({ id, label, value, sub, target }: KpiProps) {
  return (
    <div className="mkt-kpi" data-kpi={id}>
      <span className="mkt-kpi__label">{label}</span>
      <span className="mkt-kpi__value" data-testid={`kpi-${id}`}>{value}</span>
      {sub && <span className="mkt-kpi__sub">{sub}</span>}
      {target && (
        <>
          <div className="mkt-target" aria-hidden="true">
            <div className={`mkt-target__fill${target.bar.met ? ' mkt-target__fill--met' : ''}`} style={{ width: `${target.bar.fillPct}%` }} />
            <div className="mkt-target__marker" style={{ insetInlineStart: `${target.bar.markerPct}%` }} />
          </div>
          <span className="mkt-kpi__target" data-testid={`kpi-${id}-target`}>
            <b className={target.bar.met ? 'mkt-met' : 'mkt-below'}>{target.bar.met ? 'Target met' : 'Below target'}</b>
            {' · '}{target.text}
          </span>
        </>
      )}
    </div>
  );
}

/** Spec 078 AC-16 — Taps, Page loads and Tap rate for the range, with the two targets (50 taps a week, 15%). */
export default function StoreTapKpis({ stats }: { stats: StoreTapStatsResponse }) {
  const { totals, targets, from, to } = stats;
  const days = rangeDays(from, to);
  const tapTarget = scaledTapTarget(targets.tapsPerWeek, from, to);
  return (
    <div className="mkt-kpis">
      <Kpi
        id="taps"
        label="Taps"
        value={formatCount(totals.taps)}
        sub={`App Store ${formatCount(totals.tapsAppStore)} · Google Play ${formatCount(totals.tapsGooglePlay)}`}
        target={{ bar: targetBar(totals.taps, tapTarget), text: `target ${formatCount(tapTarget)} (${targets.tapsPerWeek}/week × ${days} days)` }}
      />
      <Kpi id="page-loads" label="Page loads" value={formatCount(totals.pageLoads)} sub="Share page views" />
      <Kpi
        id="tap-rate"
        label="Tap rate"
        value={formatRate(totals.tapRate)}
        sub="Taps ÷ page loads"
        target={{ bar: targetBar(totals.tapRate, targets.tapRate), text: `target ${formatRate(targets.tapRate)}` }}
      />
    </div>
  );
}
